"use client";
import React, { useEffect } from "react";
import Image from "next/image";
import { Button } from "../ui/button";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { useInView } from "react-intersection-observer";
import { ElementType } from "@/constants/tagular/main";

interface EntryPointInlineProps {
  onGetStarted?: () => void;
  headingRef?: React.Ref<HTMLHeadingElement>;
  className?: string;
}

const EntryPointInline: React.FC<EntryPointInlineProps> = ({
  onGetStarted,
  headingRef,
  className = "",
}) => {
  const ctaText = "Get started";
  const bannerWebElement: ElementViewed["webElement"] = {
    elementType: ElementType.Banner,
    text: "Your path to growth starts here",
    name: "find-your-path-inline-options",
  };

  const { elementViewed: bannerElementViewed } = useElementEvents({
    webElement: bannerWebElement,
  });

  const { ref: bannerRef, inView: bannerInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (bannerInView) {
      bannerElementViewed();
    }
  }, [bannerInView, bannerElementViewed]);

  // CTA button element tracking
  const buttonWebElement: ElementViewed["webElement"] = {
    elementType: ElementType.Button,
    text: ctaText,
    name: "find-your-path-inline-cta",
  };

  const {
    elementViewed: buttonElementViewed,
    elementClicked: buttonElementClicked,
  } = useElementEvents({ webElement: buttonWebElement });

  const { ref: buttonRef, inView: buttonInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (buttonInView) {
      buttonElementViewed();
    }
  }, [buttonInView, buttonElementViewed]);

  const handleButtonClick = () => {
    buttonElementClicked();
    onGetStarted?.();
  };

  // Inline variant (dark theme with button)
  return (
    <div
      ref={bannerRef}
      className={`container font-sans rounded-xl flex flex-col gap-4 md:flex-row md:justify-between md:items-center bg-secondary-700 text-white p-4 md:px-8 md:py-8 ${className}`}
    >
      <div className="flex flex-col gap-3 md:gap-1">
        <div className="flex items-start md:items-center gap-2">
          <Image
            src="/icons/ai-icon.png"
            alt="ai-icon"
            width={36}
            height={36}
            className="m-0 flex-shrink-0"
          />
          <h3
            ref={headingRef}
            className="text-2xl lg:text-[41px] italic font-black text-white m-0"
            tabIndex={-1}
          >
            Your path to growth starts here
          </h3>
        </div>
        <span className="text-base lg:text-lg">
          Answer a few quick questions to see online learning options built
          around your ambitions.
        </span>
      </div>
      <div className="md:ml-4 md:flex-shrink-0 mt-auto">
        <Button
          ref={buttonRef}
          variant={"brand"}
          radius="md"
          className="w-full md:w-auto lg:w-min no-underline lg:py-2 lg:px-6 focus-visible:!ring-2 focus-visible:!ring-white focus-visible:!ring-offset-2 focus-visible:!ring-offset-secondary-700"
          onClick={handleButtonClick}
        >
          {ctaText}
        </Button>
      </div>
    </div>
  );
};

export default EntryPointInline;
